import React from "react";

import { ZoomablePhoto } from "@/components/media/photo-zoomable";
import {
  ArtistBasicType,
  ArtistSpotifyEnrichmentType,
} from "@/pages/artists/schemas/artist.create.form";

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline gap-2 py-1">
      <span className="shrink-0 text-sm font-medium text-muted-foreground uppercase tracking-wide w-36">
        {label}
      </span>
      <span className="text-sm text-foreground">{value}</span>
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="text-sm font-semibold uppercase tracking-widest text-muted-foreground border-b border-border pb-1 mb-2 mt-6">
      {children}
    </h2>
  );
}

function fmt(n: number) {
  return n.toLocaleString("en-US");
}

export function SpotifyEnrichmentPreview({
  info,
  spotifyEnrichment,
}: {
  info: ArtistBasicType;
  spotifyEnrichment: ArtistSpotifyEnrichmentType;
}) {
  return (
    <div className="mx-auto ">
      {/* Header */}
      <div className="flex items-start gap-4 mb-2">
        <ZoomablePhoto
          url={spotifyEnrichment?.imageUrl || "/placeholder.svg"}
          className="rounded-md object-cover shrink-0 w-[240px] h-[240px]"
        />
        <div>
          <h1 className="text-lg font-bold text-foreground leading-tight">
            {info.name}
          </h1>
          <p className="text-sm text-muted-foreground font-mono">
            {info.spotifyId}
          </p>
        </div>
      </div>

      {/* Basic Info */}
      <SectionTitle>Basic Info</SectionTitle>
      <Field
        label="Genres"
        value={spotifyEnrichment.genres?.join(", ") || "None"}
      />
      <Field
        label="Bio"
        value={
          <span className="leading-relaxed">
            {spotifyEnrichment.bio || "N/A"}
          </span>
        }
      />
      <Field
        label="Social Links"
        value={
          spotifyEnrichment.socialLinks &&
          spotifyEnrichment.socialLinks?.length > 0
            ? spotifyEnrichment.socialLinks.map((l) => (
                <a
                  key={l.url}
                  href={l.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm underline text-foreground"
                >
                  {l.type}: {l.url}
                </a>
              ))
            : "None"
        }
      />

      {/* Stats */}
      <SectionTitle>Stats</SectionTitle>
      <Field
        label="Popularity"
        value={
          spotifyEnrichment.stats?.popularity
            ? `${spotifyEnrichment.stats?.popularity} / 100`
            : NaN
        }
      />
      <Field
        label="Followers"
        value={fmt(spotifyEnrichment.stats?.followers ?? NaN)}
      />
      <Field
        label="Monthly Listeners"
        value={fmt(spotifyEnrichment.stats?.monthlyListeners ?? NaN)}
      />

      {/* Top Cities */}
      <SectionTitle>Top Cities</SectionTitle>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-sm text-muted-foreground uppercase tracking-wide">
              <th className="py-1 pr-2 font-medium">#</th>
              <th className="py-1 pr-2 font-medium">City</th>
              <th className="py-1 pr-2 font-medium">Country</th>
              <th className="py-1 pr-2 font-medium">Region</th>
              <th className="py-1 font-medium text-right">Listeners</th>
            </tr>
          </thead>
          <tbody>
            {spotifyEnrichment?.stats?.topCities?.map((c, i) => (
              <tr key={c.city} className="border-t border-border/50">
                <td className="py-1 pr-2 font-mono text-muted-foreground">
                  {i + 1}
                </td>
                <td className="py-1 pr-2">{c.city}</td>
                <td className="py-1 pr-2 font-mono">{c.country}</td>
                <td className="py-1 pr-2 font-mono">{c.region}</td>
                <td className="py-1 text-right font-mono">
                  {fmt(c.numberOfListeners)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Gallery Images */}
      <SectionTitle>
        Gallery Images ({spotifyEnrichment.galleryImagesUrl?.length ?? 0})
      </SectionTitle>
      <div className="flex gap-2 flex-wrap">
        {spotifyEnrichment.galleryImagesUrl?.map((url, i) => (
          <div key={url} className="relative">
            <ZoomablePhoto
              url={url || "/placeholder.svg"}
              className="rounded object-cover w-[240px]"
            />
            <span className="absolute bottom-0.5 right-0.5 bg-foreground/70 text-background text-[10px] px-1 rounded font-mono">
              {i + 1}
            </span>
          </div>
        ))}
      </div>

      {/* Related Artists */}
      <SectionTitle>
        Related Artists ({spotifyEnrichment?.relatedArtists?.length ?? 0})
      </SectionTitle>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-sm text-muted-foreground uppercase tracking-wide">
              <th className="py-1 pr-2 font-medium">#</th>
              <th className="py-1 pr-2 font-medium">Image</th>
              <th className="py-1 pr-2 font-medium">Name</th>
              <th className="py-1 font-medium">Spotify ID</th>
            </tr>
          </thead>
          <tbody>
            {spotifyEnrichment?.relatedArtists?.map((a, i) => (
              <tr key={a.id} className="border-t border-border/50">
                <td className="py-1 pr-2 font-mono text-muted-foreground">
                  {i + 1}
                </td>
                <td className="py-1 pr-2">
                  <img
                    src={a.imageUrl || "/placeholder.svg"}
                    alt={a.name}
                    width={48}
                    height={48}
                    className="rounded-full object-cover"
                  />
                </td>
                <td className="py-1 pr-2 font-medium">{a.name}</td>
                <td className="py-1 font-mono text-sm text-muted-foreground">
                  {a.id}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
